import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Download, Search, Database, RefreshCw, Filter, CheckCircle2, AlertCircle, Edit3, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Combobox: dropdown with existing options + free text entry
function ComboSelect({ value, options, onSave, placeholder }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [filtered, setFiltered] = useState(options);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const q = draft.toLowerCase();
    setFiltered(q ? options.filter(o => o.toLowerCase().includes(q)) : options);
  }, [draft, options]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        if (open) save();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  });

  const startEdit = () => {
    setDraft(value || '');
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const save = () => {
    setOpen(false);
    const trimmed = draft.trim();
    if (trimmed !== (value || '').trim()) {
      onSave(trimmed);
    }
  };

  const pick = (opt) => {
    setDraft(opt);
    setOpen(false);
    if (opt !== (value || '').trim()) {
      onSave(opt);
    }
  };

  if (!open) {
    return (
      <button onClick={startEdit}
        className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-blue-50 transition-colors min-h-[26px] ${
          value ? 'text-slate-700' : 'text-slate-300 italic'
        }`}>
        {value || placeholder || 'Click to set'}
      </button>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <input ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
        className="w-full px-2 py-1 border border-blue-400 rounded text-xs outline-none bg-white"
        placeholder={placeholder} />
      {filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[180px] overflow-y-auto">
          {filtered.map(opt => (
            <button key={opt} onClick={() => pick(opt)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition-colors ${
                opt === value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700'
              }`}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Simple inline text editor for description
function InlineEdit({ value, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const save = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (value || '').trim()) {
      onSave(trimmed);
    }
  };

  if (editing) {
    return (
      <input ref={inputRef} value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        className="w-full px-2 py-1 border border-blue-400 rounded text-xs outline-none bg-white"
        placeholder={placeholder} />
    );
  }

  return (
    <span onClick={startEdit}
      className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded inline-block min-w-[60px] min-h-[22px] text-xs truncate max-w-[260px] ${
        !value ? 'text-slate-300 italic' : ''
      }`}
      title={value || 'Click to edit'}>
      {value || placeholder || '—'}
    </span>
  );
}

export default function ComponentDatabase() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [showFilter, setShowFilter] = useState('all'); // all, unassigned, assigned
  const [allGroups, setAllGroups] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (groupFilter) params.set('group', groupFilter);
      const res = await axios.get(`${API}/api/warehouse/sage-convert/components?${params}`);
      let items = res.data.components || [];

      // Apply vendor filter client-side
      if (vendorFilter) {
        items = items.filter(c => c.vendor === vendorFilter);
      }
      // Apply assignment filter
      if (showFilter === 'unassigned') {
        items = items.filter(c => !c.vendor || !c.group_name);
      } else if (showFilter === 'assigned') {
        items = items.filter(c => c.vendor && c.group_name);
      }

      setComponents(items);
      // Get all unique vendors/groups for dropdowns (from full dataset)
      const fullRes = await axios.get(`${API}/api/warehouse/sage-convert/components`);
      const allItems = fullRes.data.components || [];
      setAllGroups([...new Set(allItems.map(c => c.group_name).filter(Boolean))].sort());
      setAllVendors([...new Set(allItems.map(c => c.vendor).filter(Boolean))].sort());
    } catch (err) {
      toast.error('Failed to load components');
    }
    setLoading(false);
  }, [search, groupFilter, vendorFilter, showFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = async (itemCode, field, value) => {
    try {
      await axios.put(`${API}/api/warehouse/sage-convert/components/update`, {
        item_code: itemCode,
        [field]: value,
      });
      setComponents(prev => prev.map(c =>
        c.item_code === itemCode ? { ...c, [field]: value } : c
      ));
      // Update dropdown options if new value
      if (field === 'vendor' && value && !allVendors.includes(value)) {
        setAllVendors(prev => [...prev, value].sort());
      }
      if (field === 'group_name' && value && !allGroups.includes(value)) {
        setAllGroups(prev => [...prev, value].sort());
      }
      toast.success(`${itemCode}: ${field === 'vendor' ? 'Vendor' : 'Group'} = "${value}"`);
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/warehouse/sage-convert/import-components`, fd);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Imported: ${res.data.imported} new, ${res.data.updated} updated. Total: ${res.data.total_in_db}`);
        fetchData();
      }
    } catch (err) {
      toast.error('Import failed');
    }
    setImporting(false);
    if (importRef.current) importRef.current.value = '';
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/api/warehouse/sage-convert/export-db`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'component_database.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database exported to Excel');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleDelete = async (itemCode) => {
    try {
      await axios.delete(`${API}/api/warehouse/sage-convert/components/${encodeURIComponent(itemCode)}`);
      setComponents(prev => prev.filter(c => c.item_code !== itemCode));
      toast.success(`Deleted: ${itemCode}`);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to DELETE ALL components from the database? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/api/warehouse/sage-convert/components/all`);
      setComponents([]);
      toast.success(`Deleted ${res.data.deleted_count} components`);
    } catch (err) {
      toast.error('Delete all failed');
    }
  };

  const unassignedCount = components.filter(c => !c.vendor || !c.group_name).length;
  const totalCount = components.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Component Database
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Assign vendor &amp; group to item codes. Once set, they auto-fill in every report conversion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleDeleteAll}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-semibold transition-colors shadow-sm"
            data-testid="db-delete-all-btn">
            <X className="w-3.5 h-3.5" /> Delete All
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-semibold transition-colors shadow-sm"
            data-testid="db-export-btn">
            <Download className="w-3.5 h-3.5" /> Export Excel
          </button>
          <label className="cursor-pointer flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-semibold transition-colors shadow-sm"
            data-testid="db-import-btn">
            <Upload className="w-3.5 h-3.5" />
            {importing ? 'Importing...' : 'Import Excel'}
            <input ref={importRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleImport} disabled={importing} />
          </label>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-xs font-semibold text-slate-700">{totalCount - unassignedCount} assigned</span>
        </div>
        {unassignedCount > 0 && (
          <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-xs font-semibold text-orange-700">{unassignedCount} need assignment</span>
          </div>
        )}
        <div className="flex-1" />
        <span className="text-[11px] text-slate-400">
          Click any Vendor or Group cell to edit. Changes are saved instantly.
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, description, vendor, group..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-200 outline-none"
              data-testid="db-search" />
          </div>

          {/* Quick filters */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="db-assignment-filter">
            {[
              { key: 'all', label: 'All' },
              { key: 'unassigned', label: 'Unassigned' },
              { key: 'assigned', label: 'Assigned' },
            ].map(f => (
              <button key={f.key} onClick={() => setShowFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  showFilter === f.key
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}>
                {f.label}
              </button>
            ))}
          </div>

          <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-200 outline-none min-w-[140px]"
            data-testid="db-vendor-filter">
            <option value="">All Vendors</option>
            {allVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>

          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-200 outline-none min-w-[140px]"
            data-testid="db-group-filter">
            <option value="">All Groups</option>
            {allGroups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-340px)] overflow-y-auto">
          <table className="w-full text-xs" data-testid="db-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-800 text-white">
                <th className="px-3 py-2.5 text-left font-semibold w-36">Item Code</th>
                <th className="px-3 py-2.5 text-left font-semibold">
                  Description <span className="text-blue-300 text-[9px] font-normal ml-1">editable</span>
                </th>
                <th className="px-3 py-2.5 text-left font-semibold w-52">
                  VENDOR <span className="text-blue-300 text-[9px] font-normal ml-1">select or type</span>
                </th>
                <th className="px-3 py-2.5 text-left font-semibold w-40">
                  Group <span className="text-blue-300 text-[9px] font-normal ml-1">select or type</span>
                </th>
                <th className="px-3 py-2.5 text-center font-semibold w-10"></th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp, idx) => {
                const needsWork = !comp.vendor || !comp.group_name;
                return (
                  <tr key={comp.item_code}
                    className={`border-b border-slate-100 transition-colors ${
                      needsWork ? 'bg-orange-50/40 hover:bg-orange-50' : idx % 2 ? 'bg-slate-50/50 hover:bg-blue-50/30' : 'hover:bg-blue-50/30'
                    }`}>
                    <td className="px-3 py-1 font-mono font-bold text-slate-800">
                      {comp.item_code}
                      {needsWork && <AlertCircle className="w-3 h-3 text-orange-400 inline ml-1.5" />}
                    </td>
                    <td className="px-3 py-1 text-slate-600 max-w-[280px]">
                      <InlineEdit
                        value={comp.description}
                        onSave={val => handleUpdate(comp.item_code, 'description', val)}
                        placeholder="Click to add description"
                      />
                    </td>
                    <td className="px-1 py-0.5">
                      <ComboSelect
                        value={comp.vendor}
                        options={allVendors}
                        onSave={val => handleUpdate(comp.item_code, 'vendor', val)}
                        placeholder="Select or type vendor..."
                      />
                    </td>
                    <td className="px-1 py-0.5">
                      <ComboSelect
                        value={comp.group_name}
                        options={allGroups}
                        onSave={val => handleUpdate(comp.item_code, 'group_name', val)}
                        placeholder="Select or type group..."
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => handleDelete(comp.item_code)}
                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                        title="Delete component">
                        <X className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {components.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    {loading ? 'Loading...' : 'No components found. Import an Excel file to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
